'use client'

import { Pencil24, Plus24 } from '@vinted/monochrome-icons'
import { Cell, Divider, Icon, Image, Label, Validation } from '@vinted/web-ui'
import { useContext } from 'react'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'

import { AddressModel } from '@marketplace-web/address/address-data'

import { addressFieldConfiguration, getIsAddressComplete } from '../../utils'
import AddressDetails from '../../../AddressDetails'

type Props = {
  error: string
  onAddressDetailsClick: () => void
  address: AddressModel | null
}

const SellerAddress = ({ address, error, onAddressDetailsClick }: Props) => {
  const translate = useTranslate('conversation.get_shipping_label.modal')
  const {
    state: {
      labelOptions: { labelOptions },
    },
  } = useContext(GetShippingLabelContext)

  const asset = useAsset('assets/no-photo')

  const isAddressComplete = getIsAddressComplete(address, addressFieldConfiguration)

  const renderAddressEmptyState = () => {
    return (
      <Cell
        title={translate('sellers_empty_address_title')}
        type={Cell.Type.Navigating}
        onClick={onAddressDetailsClick}
        suffix={
          <Icon name={Plus24} testId="address-plus-icon" color={Icon.Color.GreyscaleLevel1} />
        }
        testId="seller-address-empty-state"
        validation={<Validation text={error} theme="warning" />}
      />
    )
  }

  const renderAddress = () => {
    return (
      <AddressDetails
        prefix={
          <Image
            src={labelOptions?.sender.photoUrl}
            size={Image.Size.Regular}
            styling={Image.Styling.Circle}
            fallbackSrc={asset(EMPTY_USER_IMAGE_NAME)}
          />
        }
        type={Cell.Type.Navigating}
        address={address}
        onClick={onAddressDetailsClick}
        suffix={
          <Icon name={Pencil24} color={Icon.Color.GreyscaleLevel2} testId="address-pencil-icon" />
        }
        validation={error}
      />
    )
  }

  return (
    <>
      <Label type={Label.Type.Stacked} text={translate('sellers_address_label')} />
      {isAddressComplete ? renderAddress() : renderAddressEmptyState()}
      <Divider />
    </>
  )
}

export default SellerAddress
